import { useState, useEffect, useMemo, useRef } from 'react';
import {
    Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Copy, CircleEllipsis, Pencil, Plus, Key, Trash2,
    AlertTriangle, X, Router, Import,
    Radio, Network, Globe, Layers, Server, List,
    Cctv, Monitor, HardDrive, Printer, Shield, Box, ChevronDown,
    Check, Cpu, Factory, Hash,
    Loader2,
    Columns3,
    Tag,
    ArrowUp, ArrowDown, ArrowUpDown,
    Info, Calendar, ShieldCheck as ShieldIcon, Briefcase,
    ShieldCheck,
    Eye,
    Smartphone,
    Clock,
    User,
    Download,
    ScanSearch
} from 'lucide-react';
import { createPortal } from 'react-dom';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import AddAssetModal from './AddAssetModal';
import AddCredentialModal from './AddCredentialModal';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import UploadModal from './UploadModal';
import { useClientLevel } from '../../hooks/useClientLevel';
import AddTagModal from './AddTagModal';
import { hasPermission } from '../../utils/permissionsHelper';
import AgentExportModal from '../Agent/AgentExportModal';



// --- Interfaces ---
interface Asset {
    id: number;
    agent_id: number;
    client_id: number;
    organization_id: number;
    name: string;
    asset_type: string;
    hostname: string;
    ip_address: string;
    status: string;
    os_name?: string;
    manufacturer?: string;
    mac_address?: string;
    location: string;
    switch_role: string | null;
    total_ports: number | null;
    is_primary: boolean;
    tags: string;
    resolved_tags?: string | any[];
    protocols_performed?: string | string[];
    purchase_date?: string | null;
    installation_date?: string | null;
    warranty_expiration?: string | null;
    warranty_details?: string | null;
    amc_expiration?: string | null;
    amc_provider?: string | null;
    amc_details?: string | null;
    end_of_life?: string | null;
    end_of_support?: string | null;
    is_ad_machine?: boolean;
    is_auth: boolean;
    last_seen?: string | null;
    client_name?: string | null;
    has_oob?: boolean;
    oob_type?: string;
}


interface DeleteConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    assetName: string | undefined;
    isDeleting: boolean;
}


// --- Constants & Config ---
const PRIMARY_TABS = [
    { id: 'all', label: 'All Assets', icon: Layers },
    { id: 'local_asset', label: 'EndPoint Servers', icon: Server },
    { id: 'switch', label: 'Switches', icon: Router },
    { id: 'camera', label: 'Cameras', icon: Cctv },
];


const MORE_TYPES = [
    { id: 'server', label: 'Server', icon: Server },
    { id: 'network_device', label: "Network Device", icon: Network },
    { id: 'unmanaged_switch', label: 'Unmanaged Switchs', icon: Router },
    { id: 'workstation', label: 'Workstations', icon: Monitor },
    { id: 'access_point', label: 'Access Points', icon: Radio },
    { id: 'nvr', label: 'NVR', icon: HardDrive },
    { id: 'printer', label: 'Printers', icon: Printer },
    { id: 'firewall', label: 'Firewalls', icon: Shield },
    { id: 'router', label: 'Routers', icon: Router },
    { id: 'storage', label: 'Storage Devices', icon: HardDrive },
    { id: 'mobile', label: 'Mobile', icon: Smartphone },
    { id: 'ilo', label: 'iLO', icon: Server },
    { id: 'other', label: 'Other', icon: Box }


];


const ASSET_TYPES = [
    ...PRIMARY_TABS,
    ...MORE_TYPES
];


const getAssetIconConfig = (type: string) => {
    const t = type?.toLowerCase() || '';
    const match = ASSET_TYPES.find(a => a.id !== 'all' && t.includes(a.id));
    const config = match || ASSET_TYPES.find(a => a.id === 'other')!;
    const colors: Record<string, string> = {
        camera: 'text-sky-700',
        server: 'text-sky-700',
        local_asset: 'text-sky-700',
        switch: 'text-sky-700',
        router: 'text-sky-700',
        all: 'text-sky-700',
        mobile: 'text-sky-700'
    };
    return { ...config, color: colors[config.id] || 'text-sky-700' };
};

export const getOSIcon = (type?: string) => {
    if (!type) return { icon: "/device.svg" };



    const os = type.toLowerCase();

    if (os.includes("cisco")) {
        return { icon: "/cisco.svg" };
    }
    if (
        os.includes("mac") ||
        os.includes("darwin") ||
        os === "ios" ||
        os.includes("osx")
    ) {
        return { icon: "/mac.png" };
    }

    // 🪟 Windows
    if (os.includes("windows")) {
        return { icon: "/windows.png" };
    }

    // 🐧 Linux family
    if (os.includes("ubuntu")) {
        return { icon: "/ubuntu.png" };
    }

    if (os.includes("kali")) {
        return { icon: "/kali-linux.svg" };
    }
    
    if (os.includes("centos")) {
        return { icon: "/centos.png" };
    }

    if (os.includes("almalinux")) {
        return { icon: "/AlmaLinux.svg" }
    }

    if (os.includes("suse")) {
        return { icon: "/suse.svg" }
    }

     if (os.includes("red hat")) {
        return { icon: "/redhat.svg" }
    }


    if (os.includes("fedora")) {
        return { icon: "/Fedora.svg" }
    }

    if (os.includes("debian")) {
        return { icon: "/debian.svg" }
    }


    if (
        os.includes("linux")
    ) {
        return { icon: "/linux.svg" };
    }

    
   

    // 🤖 Android
    if (os.includes("android")) {
        return { icon: "/android.svg" };
    }

    // 📦 Default
    return { icon: "/device.svg" };
};


// --- Sub-components ---
const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ open, onClose, onConfirm, assetName, isDeleting }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-3">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl max-w-xs w-full overflow-hidden transform transition-all">
                <div className="p-3">
                    <div className="flex justify-between items-start mb-2">
                        <div className="p-1 bg-red-500/20 rounded-md"><AlertTriangle className="w-4 h-4 text-gray-700 dark:text-red-500" /></div>
                        <button onClick={onClose} disabled={isDeleting} className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors disabled:opacity-50"><X className="w-4 h-4 text-gray-700 dark:text-white" /></button>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Delete Asset?</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-[11px] mb-4">Are you sure you want to delete <span className="text-gray-900 dark:text-white font-medium">"{assetName}"</span>?</p>
                    <div className="flex gap-2">
                        <button onClick={onClose} disabled={isDeleting} className="flex-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-[11px] font-medium transition-colors disabled:opacity-50">Cancel</button>
                        <button onClick={onConfirm} disabled={isDeleting} className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[11px] font-medium transition-colors flex items-center justify-center disabled:opacity-50">{isDeleting ? 'Deleting...' : 'Delete'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface ScanModalProps {
    open: boolean;
    onClose: () => void;
    asset: Asset | null;
}

const ScanModal: React.FC<ScanModalProps> = ({ open, onClose, asset }) => {
    const [jobType, setJobType] = useState('local_asset_scan');
    const [loading, setLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (open) {
            setJobType('local_asset_scan');
            setDropdownOpen(false);
        }
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!open || !asset) return null;

    const scanOptions = asset.is_ad_machine
        ? [
            { value: 'local_asset_scan', label: 'Endpoint Scan' },
            { value: 'ad_scan', label: 'AD Scan' }
        ]
        : [
            { value: 'local_asset_scan', label: 'Endpoint Scan' }
        ];

    const selectedOption = scanOptions.find(opt => opt.value === jobType) || scanOptions[0];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            const payload = {
                client_id: asset.client_id,
                agent_id: asset.agent_id,
                job_type: jobType,
                parameters: {}
            };
            const res = await baseapi.post('/agents/agent-task', payload);
            if (res.data?.status) {
                showSuccess(res.data.message || 'Scan task initiated successfully');
                onClose();
            } else {
                showError(res.data?.message || 'Failed to initiate scan task');
            }
        } catch (err: any) {
            showError(err.message || 'Error initiating scan task');
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-3">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-sm w-full overflow-hidden transform transition-all">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                            <ScanSearch className="w-4 h-4 text-gray-700 dark:text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Scan Asset</h3>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">{asset.name || asset.hostname}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                    >
                        <X className="w-4 h-4 text-gray-700 dark:text-gray-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                            Scan Option
                        </label>
                        <div className="relative" ref={dropdownRef}>
                            <button
                                type="button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                disabled={loading}
                                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg px-3 py-2.5 text-xs font-semibold text-gray-800 dark:text-white flex items-center justify-between shadow-sm cursor-pointer transition-colors"
                            >
                                <span>{selectedOption?.label}</span>
                                <ChevronDown className={`w-3.5 h-3.5 text-gray-700 dark:text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-gray-900 dark:text-emerald-400' : ''}`} />
                            </button>

                            {dropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-20 overflow-hidden py-1">
                                    {scanOptions.map((opt) => (
                                        <div
                                            key={opt.value}
                                            onClick={() => {
                                                setJobType(opt.value);
                                                setDropdownOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium cursor-pointer transition-colors ${jobType === opt.value
                                                ? 'bg-emerald-50 dark:bg-emerald-600/20 text-emerald-600 dark:text-emerald-400 font-bold'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                                }`}
                                        >
                                            <span>{opt.label}</span>
                                            {jobType === opt.value && <Check className="w-3.5 h-3.5 text-gray-700 dark:text-emerald-400" />}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-emerald-900/20"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Scanning...
                                </>
                            ) : (
                                'Start Scan'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};


// --- Main Component ---
const AssetsTable = () => {
    const level = useClientLevel();
    const canCreate = hasPermission('assets', 'create');
    const canDelete = hasPermission('assets', 'delete');
    const canEdit = hasPermission('assets', 'update');
    const [searchParams, setSearchParams] = useSearchParams();
    const rawType = searchParams.get('asset_type') || 'all';
    const prevLevelIdRef = useRef(level?.id);
    const rawAgentId = searchParams.get('agent_id');
    const agentId = prevLevelIdRef.current !== level?.id ? null : rawAgentId;

    const typeFilter = rawType;
    const statusFilter = (searchParams.get('status') as 'all' | 'active' | 'offline') || 'all';
    const isAuthFilter = searchParams.get('is_auth');

    // Clear agent_id and reset page to 1 from URL search params when client level changes
    useEffect(() => {
        if (prevLevelIdRef.current !== level?.id) {
            prevLevelIdRef.current = level?.id;
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                let hasChanges = false;

                if (next.has('agent_id')) {
                    next.delete('agent_id');
                    hasChanges = true;
                }

                if (next.get('page') !== '1') {
                    next.set('page', '1');
                    hasChanges = true;
                }

                return hasChanges ? next : prev;
            }, { replace: true });
        }
    }, [level?.id, setSearchParams]);


    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [assetsData, setAssetsData] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const currentPage = parseInt(searchParams.get('page') || '1', 10) || 1;
    const itemsPerPage = parseInt(searchParams.get('limit') || '30', 10) || 30;

    const sortBy = (() => {
        const urlSortBy = searchParams.get('sort_by');
        if (urlSortBy) return urlSortBy;

        const stored = localStorage.getItem('organization_settings');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.assetDefaultSortKey) {
                    return parsed.assetDefaultSortKey;
                }
            } catch (e) {
                console.error("Failed to parse organization_settings for asset sortBy", e);
            }
        }
        return 'last_seen';
    })();

    const sortOrder = (() => {
        const urlSortOrder = searchParams.get('sort_order') as 'asc' | 'desc';
        if (urlSortOrder) return urlSortOrder;

        const stored = localStorage.getItem('organization_settings');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.assetDefaultSortDir) {
                    return parsed.assetDefaultSortDir;
                }
            } catch (e) {
                console.error("Failed to parse organization_settings for asset sortOrder", e);
            }
        }
        return 'desc';
    })();

    const [showExportModal, setShowExportModal] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [total, setTotal] = useState(0);


    // Column visibility state for all columns
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
        asset_name: true,
        os: true,
        manufacturer: true, // Initially hidden
        mac: true,
        ip_address: true,
        hostname: true,
        type: true,
        status: true,
        tags: true,
        protocols: true,
        last_frame: true,
        last_seen: true,
        client_name: true,
    });
    const [colMenuOpen, setColMenuOpen] = useState(false);

    const [settingsVersion, setSettingsVersion] = useState(0);

    useEffect(() => {
        const handleSettingsChange = () => {
            setSettingsVersion(prev => prev + 1);
        };
        window.addEventListener('organization-settings-changed', handleSettingsChange);
        return () => window.removeEventListener('organization-settings-changed', handleSettingsChange);
    }, []);

    const baseColumns = useMemo(() => {
        const defaultCols = [
            { key: 'asset_name', label: 'Asset Name', icon: Server, color: 'text-gray-700 dark:text-cyan-400', sortKey: 'name' },
            { key: 'hostname', label: 'Hostname', icon: Globe, color: 'text-gray-700 dark:text-emerald-400', sortKey: 'hostname' },
            { key: 'ip_address', label: 'IP Address', icon: Network, color: 'text-gray-700 dark:text-yellow-400', sortKey: 'ip_address' },
            { key: 'mac', label: 'MAC Address', icon: Hash, color: 'text-gray-700 dark:text-orange-400', sortKey: 'mac_address' },
            { key: 'os', label: 'OS', icon: Cpu, color: 'text-gray-700 dark:text-blue-400', sortKey: 'os_name' },
            { key: 'manufacturer', label: 'Manufacturer', icon: Factory, color: 'text-gray-700 dark:text-pink-400', sortKey: 'manufacturer' },
            { key: 'type', label: 'Type', icon: Layers, color: 'text-gray-700 dark:text-indigo-400', sortKey: 'asset_type' },
            { key: 'status', label: 'Status', icon: ShieldCheck, color: 'text-gray-700 dark:text-green-400', sortKey: 'status' },
            { key: 'tags', label: 'Tags', icon: Tag, color: 'text-gray-700 dark:text-purple-400', sortKey: 'tags' },
            { key: 'last_seen', label: 'Last Scan', icon: Clock, color: 'text-gray-700 dark:text-purple-400', sortKey: 'last_seen' },
            { key: 'client_name', label: 'Client Name', icon: User, color: 'text-gray-700 dark:text-purple-400', sortKey: 'client_name' },
            { key: 'protocols', label: 'Protocols', icon: Radio, color: 'text-gray-700 dark:text-purple-400', sortKey: 'protocols_performed' },
            { key: 'last_frame', label: 'Last Frame', icon: Eye, color: 'text-gray-700 dark:text-teal-400' },
            { key: 'actions', label: 'Actions', icon: List, color: 'text-gray-700 dark:text-gray-500' }
        ];

        const stored = localStorage.getItem('organization_settings');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.assetColumnOrder && Array.isArray(parsed.assetColumnOrder)) {
                    const ordered: typeof defaultCols = [];
                    parsed.assetColumnOrder.forEach((k: string) => {
                        const found = defaultCols.find(c => c.key === k);
                        if (found) ordered.push(found);
                    });
                    defaultCols.forEach(c => {
                        if (!ordered.some(o => o.key === c.key)) {
                            ordered.push(c);
                        }
                    });
                    return ordered;
                }
            } catch (e) {
                console.error("Failed to parse organization_settings for asset column ordering", e);
            }
        }
        return defaultCols;
    }, [settingsVersion]);

    const columns = useMemo(() => {
        return baseColumns.filter(col => {
            if (col.key !== 'actions' && !visibleColumns[col.key]) {
                return false;
            }
            if (col.key === 'client_name' && level?.name !== 'global') {
                return false;
            }
            if (col.key === 'last_frame' && typeFilter !== 'camera') {
                return false;
            }
            if (col.key === 'actions' && !(canDelete || canEdit)) {
                return false;
            }
            return true;
        });
    }, [baseColumns, visibleColumns, level, typeFilter, canDelete, canEdit]);


    const [assetModalOpen, setAssetModalOpen] = useState(false);
    const [assetModalMode, setAssetModalMode] = useState<'add' | 'edit'>('add');
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [importModal, setImportModal] = useState(false);
    const [credentialModalOpen, setCredentialModalOpen] = useState(false);
    const [credentialAssetId, setCredentialAssetId] = useState<number | null>(null);
    const [credentialClientId, setCredentialClientId] = useState<any>(null);
    const [initialAgentId, setInitialAgentId] = useState<number | null>(null);
    const [isCamera, setIsCamera] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [tagAssetId, setTagAssetId] = useState<number | null>(null);
    const [tagAssetName, setTagAssetName] = useState<string>('');
    const [scanModalOpen, setScanModalOpen] = useState(false);
    const [selectedScanAsset, setSelectedScanAsset] = useState<Asset | null>(null);
    const [infoPopoverAsset, setInfoPopoverAsset] = useState<Asset | null>(null);
    const [lastFrameModalOpen, setLastFrameModalOpen] = useState(false);
    const [lastFrameImage, setLastFrameImage] = useState<string | null>(null);
    const [lastFrameLoading, setLastFrameLoading] = useState(false);
    const [lastFrameCapturedAt, setLastFrameCapturedAt] = useState<string | null>(null);
    const [lastFrameAssetName, setLastFrameAssetName] = useState<string>('');
    const currentPath = useLocation();




    const navigate = useNavigate();
    const [expandedProtocols, setExpandedProtocols] = useState<Record<string, boolean>>({});
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;


    const handleTypeFilterChange = (val: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (!val || val === 'all') next.delete('asset_type');
            else next.set('asset_type', val);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setMoreDropdownOpen(false);
    };


    const handleStatusFilterChange = (val: 'all' | 'active' | 'offline' | 'no_auth') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'no_auth') {
                next.set('is_auth', 'false');
                next.delete('status');
            } else {
                next.delete('is_auth');
                if (val === 'all') next.delete('status');
                else next.set('status', val);
            }
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const clearAgentFilter = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('agent_id');
            next.set('page', '1');
            return next;
        }, { replace: true });
    };




    const toggleColumn = (col: string) => {
        setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
    };


    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('page', '1');
                    return next;
                }, { replace: true });
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (openMenuId && !target.closest('[data-menu-id]')) setOpenMenuId(null);
            if (moreDropdownOpen && !target.closest('[data-more-dropdown]')) setMoreDropdownOpen(false);
            if (colMenuOpen && !target.closest('[data-col-menu]')) setColMenuOpen(false);
            if (infoPopoverAsset && !target.closest('[data-info-popover]')) setInfoPopoverAsset(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openMenuId, moreDropdownOpen, colMenuOpen, infoPopoverAsset]);


    const fetchData = async (skip: number = 0, limit: number = itemsPerPage) => {
        try {
            setLoading(true);
            const queryObj: any = level?.id ? { client_id: level.id } : {};
            const apiAssetType =
                 typeFilter === "other"
                        ? "unknown"
                        : typeFilter
            if (apiAssetType !== 'all') {
                queryObj.asset_type = apiAssetType;
            } else {
                if (isAuthFilter) {
                    queryObj.asset_type = { $notin: ['isp', 'external_asset'], $in: ['camera', 'switch', 'ilo'] };
                } else {
                    queryObj.asset_type = { $notin: ['isp', 'external_asset'] };
                }
            }
            if (statusFilter !== 'all') {
                queryObj.status = statusFilter;
                // if (statusFilter == 'offline') {
                //     queryObj.is_auth = true;
                // }
            };
            if (isAuthFilter) {
                queryObj.is_auth = false;
            }
            if (agentId) {
                queryObj.agent_id = Number(agentId);
            }
            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { "$ilike": `%${debouncedSearch}%` } },
                    { ip_address: { "$ilike": `%${debouncedSearch}%` } },
                    { hostname: { "$ilike": `%${debouncedSearch}%` } },
                    { mac_address: { "$ilike": `%${debouncedSearch}%` } },
                    { os_name: { "$ilike": `%${debouncedSearch}%` } },
                    { platform: { "$ilike": `%${debouncedSearch}%` } },
                    { resolved_tags:{$ilike:`%${debouncedSearch}%`}}
                ];
            }


            const params: any = {
                query: JSON.stringify(queryObj),
                skip, limit,
                sort_by: sortBy, sort_order: sortOrder,
            };


            const response = await baseapi.get('/assets', { params });
            if (response.data.status) {
                setAssetsData(response.data.data);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
                setTotal(response.data.total);
            } else {
                showError(response.data.message);
                setAssetsData([]); setTotalPages(0); setTotalItems(0);
            }
        } catch (error: any) { showError(error.message); }
        finally { setLoading(false); }
    };


    useEffect(() => {
        fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, typeFilter, statusFilter, isAuthFilter, debouncedSearch, level, sortBy, sortOrder, rawAgentId]);


    const handleRefresh = () => fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);


    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) { console.error('Failed to copy: ', err); }
    };


    const getStatusColor = (status: string) => {
        const s = status?.toLowerCase();
        if (s === 'active') return 'text-green-700 dark:text-green-400';
        if (s === 'offline') return 'text-red-500';
        return 'text-gray-400';
    };


    const getStatusBg = (status: string) => {
        const s = status?.toLowerCase();
        if (s === 'active') return 'bg-green-700 dark:bg-green-400';
        if (s === 'offline') return 'bg-red-500 shadow-[0_0_5px_rgba(239,68,68,0.8)]';
        return 'bg-gray-400';
    };


    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };
    const handleItemsPerPageChange = (value: number) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-700 dark:text-gray-500 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-gray-700 dark:text-orange-500" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-gray-700 dark:text-orange-500" />;
    };


    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX - 100 });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };


    const handleInfoClick = (e: React.MouseEvent, asset: Asset) => {
        e.stopPropagation();
        setInfoPopoverAsset(asset);
    };

    const handleScanClick = async (asset: Asset) => {
        setOpenMenuId(null);
        if (asset.asset_type === 'local_asset') {
            setSelectedScanAsset(asset);
            setScanModalOpen(true);
        } else {
            try {
                const assetTypeLower = asset.asset_type?.toLowerCase() || '';
                let jobType = 'asset_discovery';
                if (assetTypeLower.includes('camera') && asset.is_auth) {
                    jobType = 'camera_scan';
                } else if (assetTypeLower.includes('switch') && asset.is_auth) {
                    jobType = 'snmp_scan';
                } else if (assetTypeLower.includes('firewall') && asset.is_auth) {
                    jobType = 'firewall_scan';
                }

                const payload = {
                    client_id: asset.client_id,
                    agent_id: asset.agent_id,
                    job_type: jobType,
                    parameters: {
                        asset_id: asset.id,
                        asset_type: asset.asset_type,
                        ip_address: asset.ip_address || '',
                        mac_address: asset.mac_address || ''
                    }
                };
                const res = await baseapi.post('/agents/agent-task', payload);
                if (res.data?.status) {
                    showSuccess(res.data.message || 'Scan task initiated successfully');
                } else {
                    showError(res.data?.message || 'Failed to initiate scan task');
                }
            } catch (err: any) {
                showError(err.message || 'Error initiating scan task');
            }
        }
    };

    const handleEdit = (asset: Asset) => {
        setSelectedAsset(asset); setAssetModalMode('edit'); setAssetModalOpen(true);
        setInitialAgentId(asset.agent_id); setIsCamera(asset.asset_type === 'camera');
        setOpenMenuId(null);
    };


    const handleAssetClick = (has_oob: boolean = false, oob_type: string = "", is_ad_machine: boolean = false, name: string, assetId: any, client_id: any, agent_id: any, organization_id: any, assettype: any, hostname: string, path: string = `${currentPath.pathname}${currentPath.search}`) => {
        sessionStorage.setItem('internal_ids', JSON.stringify({ 'is_ad_machine': is_ad_machine, 'asset_id': assetId, 'client_id': client_id, 'agent_id': agent_id, 'organization_id': organization_id, asset_type: assettype, host_name: hostname, path: path, name: name, has_oob: has_oob, oob_type: oob_type }));
        navigate("/asset/dashboard");

    }


    const handleCredentials = (asset: Asset) => {
        setCredentialAssetId(asset.id); setCredentialClientId(asset.client_id);
        setCredentialModalOpen(true); setIsCamera(asset.asset_type === 'camera');
        setOpenMenuId(null);
    };

    const handleAddTags = (asset: Asset) => {
        setTagAssetId(asset.id);
        setTagAssetName(asset.name);
        setTagModalOpen(true);
        setOpenMenuId(null);
    };

    const fetchLastFrame = async (clientId: number, assetId: number, assetName: string) => {
        try {
            setLastFrameLoading(true);
            setLastFrameModalOpen(true);
            setLastFrameImage(null);
            setLastFrameCapturedAt(null);
            setLastFrameAssetName(assetName);
            const res = await baseapi.get('/camera_frames', {
                params: { query: JSON.stringify({ client_id: clientId, asset_id: assetId }) }
            });
            if (res.data.status && res.data.data && res.data.data.length > 0) {
                setLastFrameImage(res.data.data[0].last_frame);
                setLastFrameCapturedAt(res.data.data[0].captured_at || null);
            } else {
                setLastFrameImage(null);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch camera frame');
            setLastFrameImage(null);
        } finally {
            setLastFrameLoading(false);
        }
    };


    const handleDeleteClick = (asset: Asset) => { setAssetToDelete(asset); setDeleteModalOpen(true); setOpenMenuId(null); };


    const confirmDelete = async () => {
        if (!assetToDelete) return;
        try {
            setIsDeleting(true);
            const res = await baseapi.delete(`/assets/${assetToDelete.id}`);
            if (res.data.status) { showSuccess('Asset deleted successfully'); handleRefresh(); }
            else showError(res.data.message || 'Failed to delete asset');
        } catch (error: any) { showError(error.message); }
        finally { setIsDeleting(false); setDeleteModalOpen(false); setAssetToDelete(null); }
    };

    const formatDate = (ts: string | null | undefined) => {
        if (!ts) return "-";

        const fixed = ts.slice(0, 23) + "Z"; // trim microseconds + add UTC
        return new Date(fixed).toLocaleString();
    };

    return (
        <div className="text-gray-800 dark:text-gray-100 p-3 pb-20 min-h-screen transition-colors duration-200">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg"><Router className="w-4 h-4 text-white" /></div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="font-bold text-lg text-gray-900 dark:text-gray-100 leading-none transition-colors duration-200">Assets</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {searchParams.get('agent_id') && (
                            <button
                                onClick={clearAgentFilter}
                                className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 text-xs font-bold rounded-lg transition-colors duration-200 cursor-pointer"
                            >
                                <X className="w-3.5 h-3.5 text-gray-700 dark:text-red-400" />
                                <span>Clear Agent Filter</span>
                            </button>
                        )}

                        <button onClick={handleRefresh} disabled={loading} className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors duration-200"><RefreshCw className={`w-3.5 h-3.5 font-semibold text-gray-700 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} /></button>

                        {/* Column Visibility Trigger with All Columns */}

                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-300 w-3.5 h-3.5 transition-colors duration-200" />
                            <input type="text" placeholder="Search by name, IP, hostname..." className="w-full bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-700 dark:placeholder-gray-500 focus:border-orange-500 outline-none shadow-sm transition-colors duration-200" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>



                        <div className="relative" data-col-menu>
                            <button onClick={() => setColMenuOpen(!colMenuOpen)} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm transition-colors duration-200 flex items-center gap-2">
                                <Columns3 className="w-3.5 h-3.5 text-gray-700 dark:text-gray-200" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {colMenuOpen && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto transition-colors duration-200">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-100 dark:border-gray-800 mb-1 transition-colors duration-200">Toggle Columns</div>
                                    {[
                                        { id: 'asset_name', label: 'Asset Name', icon: Server },
                                        { id: 'os', label: 'Operating System', icon: Cpu },
                                        { id: 'manufacturer', label: 'Manufacturer', icon: Factory },
                                        { id: 'mac', label: 'MAC Address', icon: Hash },
                                        { id: 'ip_address', label: 'IP Address', icon: Network },
                                        { id: 'hostname', label: 'Hostname', icon: Globe },
                                        { id: 'type', label: 'Asset Type', icon: Layers },
                                        { id: 'status', label: 'Status', icon: ShieldCheck },
                                        { id: 'tags', label: 'Tags', icon: Tag },
                                        { id: 'last_seen', label: 'Last Seen', icon: Clock },
                                        { id: 'client_name', label: 'Client Name', icon: User },
                                        { id: 'protocols', label: 'Protocols', icon: Radio },
                                        ...(typeFilter === 'camera' ? [{ id: 'last_frame', label: 'Last Frame', icon: Eye }] : []),
                                    ].map(col => (
                                        <button key={col.id} onClick={() => toggleColumn(col.id)} className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200">
                                            <div className="flex items-center gap-2.5"><col.icon className={`w-3 h-3 ${visibleColumns[col.id] ? 'text-gray-700 dark:text-orange-500' : 'text-gray-400 dark:text-gray-600'}`} />{col.label}</div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors duration-200 ${visibleColumns[col.id] ? 'bg-orange-600 border-orange-600' : 'border-gray-300 dark:border-gray-700'}`}>
                                                {visibleColumns[col.id] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {level?.id && canCreate && (
                            <div className="flex items-center gap-2">
                                <button onClick={() => { setSelectedAsset(null); setAssetModalMode('add'); setAssetModalOpen(true); }} className="flex items-center gap-1.5 bg-orange-600 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-orange-700 shadow-lg shadow-orange-900/20 transition-colors duration-200"><Plus className="w-4 h-4" /> Add Asset</button>
                                <button onClick={() => setImportModal(true)} className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-gray-800 dark:text-white font-bold hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors duration-200"><Import className="w-4 h-4 text-gray-700 dark:text-orange-500" /> Import</button>
                            </div>
                        )}
                        <button
                            onClick={() => setShowExportModal(true)}
                            className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-gray-800 dark:text-white font-bold hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors duration-200"
                        >
                            <Download className="w-4 h-4 text-gray-700 dark:text-cyan-500" />
                            Export
                        </button>
                    </div>
                </div>


                {/* Filter Tabs Row */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-1 py-0.5 bg-white/80 dark:bg-gray-800/30 rounded-t-xl border border-gray-200 dark:border-gray-800 shadow-sm transition-colors duration-200">
                    <div className="flex items-center gap-1 flex-wrap">
                        {PRIMARY_TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => handleTypeFilterChange(tab.id)}
                                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-[11px] font-bold transition-colors duration-200 ${typeFilter === tab.id
                                    ? 'bg-orange-600 text-white shadow-md shadow-orange-500/20'
                                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                <tab.icon className="w-3.5 h-3.5" />
                                {tab.label}
                                {typeFilter === tab.id && !loading ? (
                                    <span className="text-xs">({total})</span>
                                ) : typeFilter === tab.id && loading ? (
                                    <span className="animate-spin"><Loader2 className="w-3.5 h-3.5 text-gray-700 dark:text-orange-500" /></span>
                                ) : (
                                    <span className="text-xs text-gray-400"></span>
                                )}
                            </button>
                        ))}


                        {/* More Dropdown Tab */}
                        <div className="relative" data-more-dropdown>
                            <button
                                onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
                                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-[11px] font-bold transition-colors duration-200 ${MORE_TYPES.some(m => m.id === typeFilter)
                                    ? 'bg-orange-50 dark:bg-gray-700 text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-500/30'
                                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                <List className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />

                                {MORE_TYPES.find(m => m.id === typeFilter)?.label || 'More'}

                                {MORE_TYPES.some(m => m.id === typeFilter) && !loading ? (
                                    <span className="text-xs">({total})</span>
                                ) : MORE_TYPES.some(m => m.id === typeFilter) && loading ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-700 dark:text-orange-500" />
                                ) : null}

                                <ChevronDown
                                    className={`w-3 h-3 text-gray-700 dark:text-gray-400 transition-transform ${moreDropdownOpen ? 'rotate-180' : ''
                                        }`}
                                />
                            </button>


                            {moreDropdownOpen && (
                                <div className="absolute top-full mt-2 left-0 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[100] overflow-hidden py-1 transition-colors duration-200">
                                    {MORE_TYPES.map(item => (
                                        <button
                                            key={item.id}
                                            onClick={() => handleTypeFilterChange(item.id)}
                                            className={`w-full flex items-center gap-3 px-4 py-2 text-[11px] font-medium transition-colors duration-200 ${typeFilter === item.id ? 'bg-orange-50 dark:bg-orange-600/10 text-orange-600 dark:text-orange-400' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                                }`}
                                        >
                                            <item.icon className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>


                    <div className="flex items-center bg-gray-100 dark:bg-gray-900/50 rounded-lg p-1 border border-gray-200 dark:border-gray-800 transition-colors duration-200">
                        {(() => {
                            const showNoAuthTab = ['all', 'camera', 'switch', 'ilo'].includes(typeFilter);
                            const statusOptions = showNoAuthTab
                                ? ['all', 'active', 'offline', 'no_auth']
                                : ['all', 'active', 'offline'];

                            return statusOptions.map((s) => {
                                const isNoAuthSelected = isAuthFilter === 'false';
                                const isSelected = s === 'no_auth' ? isNoAuthSelected : (!isNoAuthSelected && statusFilter === s);

                                let activeClasses = '';

                                if (isSelected) {
                                    if (s === 'active') {
                                        activeClasses = 'bg-green-500/15 text-green-600 dark:bg-green-600/30 dark:text-green-400 border border-green-500/30';
                                    } else if (s === 'offline') {
                                        activeClasses = 'bg-red-500/15 text-red-600 dark:bg-red-600/30 dark:text-red-400 border border-red-500/30';
                                    } else if (s === 'no_auth') {
                                        activeClasses = 'bg-orange-500/15 text-orange-600 dark:bg-orange-600/30 dark:text-orange-400 border border-orange-500/30';
                                    } else {
                                        activeClasses = 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm';
                                    }
                                }

                                return (
                                    <button
                                        key={s}
                                        onClick={() => handleStatusFilterChange(s as any)}
                                        className={`px-4 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors duration-200
                                            ${isSelected
                                                ? activeClasses
                                                : s === "active"
                                                    ? "text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                                                    : s === "offline"
                                                        ? "text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                                        : s === "no_auth"
                                                            ? "text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
                                                            : "text-gray-500 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
                                            }
                                        `}
                                    >
                                        {s === 'no_auth' ? 'no auth' : s}
                                    </button>
                                );
                            });
                        })()}
                    </div>
                </div>


                {/* Table View Body */}
                <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-b-xl overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10 transition-colors duration-200">
                                <tr className="border-b border-gray-200 dark:border-gray-700/50 transition-colors duration-200">
                                    {columns.map((col) => {
                                        if (col.key === 'actions') {
                                            return (
                                                <th key={col.key} className="px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center transition-colors duration-200">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {col.label}
                                                    </div>
                                                </th>
                                            );
                                        }

                                        if (col.sortKey) {
                                            return (
                                                <th
                                                    key={col.key}
                                                    className="px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors duration-200"
                                                    onClick={() => handleSort(col.sortKey!)}
                                                >
                                                    <div className="flex items-center">
                                                        {col.label}
                                                        <SortIcon field={col.sortKey!} />
                                                    </div>
                                                </th>
                                            );
                                        }

                                        return (
                                            <th key={col.key} className="px-1 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest transition-colors duration-200">
                                                <div className="flex items-center gap-2">
                                                    {col.label}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 transition-colors duration-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="py-16 text-center text-gray-600 dark:text-gray-200 font-bold uppercase tracking-widest italic transition-colors duration-200">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-gray-700 dark:text-orange-500" />
                                                Loading assets
                                            </div>
                                        </td>
                                    </tr>
                                ) : assetsData.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="py-16 text-center text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest italic transition-colors duration-200">
                                            No matching assets found.
                                        </td>
                                    </tr>
                                ) : (
                                    assetsData.map((item) => {
                                        const config = getAssetIconConfig(item.asset_type);
                                        const { icon } = getOSIcon(item.os_name);
                                        const Icon = config.icon;

                                        return (
                                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-200 group border-b border-gray-100 dark:border-gray-700/30">
                                                {columns.map((col) => {
                                                    if (col.key === 'asset_name') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <div onClick={() => handleAssetClick(item.has_oob, item.oob_type, item.is_ad_machine, item.name, item.id, item.client_id, item.agent_id, item.organization_id, item.asset_type, item.hostname)} className="flex items-center cursor-pointer gap-3">
                                                                    <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-bold text-gray-900 dark:text-gray-200 hover:text-orange-600 dark:hover:text-blue-400" title={item.name}>{item.name || 'Unnamed'}</span>
                                                                        {item.is_primary && <span className="text-[8px] font-bold text-orange-600 dark:text-orange-400 bg-orange-500/10 px-1 rounded border border-orange-500/20 uppercase">PRI</span>}
                                                                        <button
                                                                            data-info-popover={item.id}
                                                                            onClick={(e) => handleInfoClick(e, item)}
                                                                            className="text-gray-700 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400 cursor-pointer transition-colors p-0.5"
                                                                        >
                                                                            <Info size={11} className="text-gray-700 dark:text-gray-300" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'hostname') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5 font-mono">
                                                                <div className="flex items-center gap-2 group/copy">
                                                                    <span className="text-gray-700 dark:text-gray-100 text-[10px] leading-none truncate max-w-[130px]" title={item.hostname}>
                                                                        {item.hostname || '-'}
                                                                    </span>
                                                                    {item.hostname && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                copyToClipboard(item.hostname, `host-${item.id}`);
                                                                            }}
                                                                            className="opacity-0 group-hover/copy:opacity-100 transition-opacity"
                                                                        >
                                                                            {copiedField === `host-${item.id}` ? (
                                                                                <Check className="w-2.5 h-2.5 text-gray-700 dark:text-green-400" />
                                                                            ) : (
                                                                                <Copy className="w-2.5 h-2.5 text-gray-700 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white" />
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'ip_address') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5 font-mono">
                                                                <div className="flex items-center gap-2 group/copy">
                                                                    <span className="text-gray-800 dark:text-gray-300 text-[11px] font-bold leading-none">
                                                                        {item.ip_address || '-'}
                                                                    </span>
                                                                    {item.ip_address && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                copyToClipboard(item.ip_address, `ip-${item.id}`);
                                                                            }}
                                                                            className="opacity-0 group-hover/copy:opacity-100 transition-opacity"
                                                                        >
                                                                            {copiedField === `ip-${item.id}` ? (
                                                                                <Check className="w-2.5 h-2.5 text-gray-700 dark:text-green-400" />
                                                                            ) : (
                                                                                <Copy className="w-2.5 h-2.5 text-gray-700 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white" />
                                                                            )}
                                                                        </button>
                                                                    )}
                                                              </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'mac') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <span className="text-[10px] text-gray-700 dark:text-gray-100 font-mono">{item.mac_address || '-'}</span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'os') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <div className="flex items-center gap-1">
                                                                    <img src={icon} alt="os" className="w-5 h-5.5" />
                                                                    <span className="text-[10px] text-gray-800 dark:text-gray-100 font-medium">
                                                                        {item.os_name || "Generic OS"}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'manufacturer') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <span className="text-[10px] text-gray-700 dark:text-gray-100 font-medium pl-1">{item.manufacturer || '---'}</span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'type') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <span className="text-[10px] text-gray-700 dark:text-gray-100 uppercase tracking-tight font-medium">
                                                                    {item.asset_type === 'local_asset'
                                                                        ? 'EndPoint Server'
                                                                        : (item.asset_type?.replace(/_/g, ' ') || 'Other')}
                                                                </span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'status') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-900/50">
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusBg(item.status)} ${item.status?.toLowerCase() === 'offline' ? 'animate-pulse' : ''}`} />
                                                                    <span className={`text-[9px] font-bold uppercase tracking-wide ${getStatusColor(item.status)}`}>{item.status || 'Unknown'}</span>
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'tags') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                                    {(() => {
                                                                        try {
                                                                            if (!item.resolved_tags) return null;
                                                                            const tags = typeof item.resolved_tags === 'string' ? JSON.parse(item.resolved_tags) : item.resolved_tags;
                                                                            if (!Array.isArray(tags)) return null;
                                                                            return tags.map((t: any) => (
                                                                                <span key={t.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold bg-purple-50 dark:bg-purple-600/10 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 uppercase tracking-tighter">
                                                                                    {t.name}
                                                                                </span>
                                                                            ));
                                                                        } catch { return null; }
                                                                    })()}
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'last_seen') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <span className="text-[10px] text-gray-700 dark:text-gray-100 font-medium">
                                                                    {formatDate(item.last_seen)}
                                                                </span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'client_name') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <span className="text-[10px] text-gray-700 dark:text-gray-100 font-medium">
                                                                    {item.client_name}
                                                                </span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'protocols') {
                                                        const parsedProtocols = (() => {
                                                            try {
                                                                if (!item.protocols_performed) return [];
                                                                return Array.isArray(item.protocols_performed) ? item.protocols_performed : JSON.parse(item.protocols_performed);
                                                            } catch { return []; }
                                                        })();
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {parsedProtocols.length > 0 ? (
                                                                        <>
                                                                            {(expandedProtocols[item.id]
                                                                                ? parsedProtocols
                                                                                : parsedProtocols.slice(0, 4)
                                                                            ).map((p: string, idx: number) => (
                                                                                <span
                                                                                    key={idx}
                                                                                    className="text-[9px] font-mono bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600/50 text-gray-700 dark:text-gray-200 font-semibold px-1.5 py-0.5 rounded uppercase"
                                                                                >
                                                                                    {p}
                                                                                </span>
                                                                            ))}

                                                                            {parsedProtocols.length > 4 && (
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setExpandedProtocols(prev => ({
                                                                                            ...prev,
                                                                                            [item.id]: !prev[item.id]
                                                                                        }));
                                                                                    }}
                                                                                    className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold"
                                                                                >
                                                                                    {expandedProtocols[item.id]
                                                                                        ? "Show less"
                                                                                        : `(+${parsedProtocols.length - 4} more)`
                                                                                    }
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-gray-400 dark:text-gray-600 font-mono">-</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'last_frame') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5 text-center">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        fetchLastFrame(item.client_id, item.id, item.name);
                                                                    }}
                                                                    className="p-1.5 hover:bg-teal-500/10 dark:hover:bg-teal-600/20 rounded-lg text-gray-700 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors duration-200"
                                                                    title="View Last Frame"
                                                                >
                                                                    <Eye className="w-4 h-4 text-gray-700 dark:text-teal-400" />
                                                                </button>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'actions') {
                                                        return (
                                                            <td key={col.key} className="px-1 py-0.5 text-center">
                                                                <button data-menu-id={item.id} onClick={(e) => handleMenuOpen(e, item.id)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-100 hover:text-gray-800 dark:hover:text-white transition-colors duration-200"><CircleEllipsis className="w-4.5 h-4.5 text-gray-700 dark:text-gray-100" /></button>
                                                            </td>
                                                        );
                                                    }

                                                    return null;
                                                })}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>


                    {/* Pagination */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between transition-colors duration-200">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown value={itemsPerPage} onChange={handleItemsPerPageChange} />
                            <span className="text-[10px] text-gray-600 dark:text-gray-200 font-bold uppercase tracking-widest transition-colors duration-200">
                                <span className="text-gray-900 dark:text-gray-200">{startIndex + 1}</span> - <span className="text-gray-900 dark:text-gray-200">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-700 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronsLeft className="w-4 h-4 text-gray-700 dark:text-gray-500" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-700 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-500" /></button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-gray-700 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-500" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-gray-700 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white disabled:opacity-30 transition-colors duration-200"><ChevronsRight className="w-4 h-4 text-gray-700 dark:text-gray-500" /></button>
                        </div>
                    </div>
                </div>
            </div>


            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeAsset = assetsData.find(a => String(a.id) === openMenuId);
                    return (
                        <div className="fixed w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden py-1.5 transition-colors duration-200" style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
                            {canEdit && activeAsset && activeAsset.asset_type !== 'unmanaged_switch' && (
                                <button
                                    onClick={() => handleScanClick(activeAsset)}
                                    className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors duration-200"
                                >
                                    <ScanSearch className="w-3.5 h-3.5 text-gray-700 dark:text-emerald-400" /> Scan
                                </button>
                            )}
                            {canEdit && <button onClick={() => handleEdit(activeAsset!)} className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors duration-200"><Pencil className="w-3.5 h-3.5 text-gray-700 dark:text-blue-400" /> Edit Asset</button>}
                            {canEdit && <button onClick={() => handleCredentials(activeAsset!)} className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors duration-200"><Key className="w-3.5 h-3.5 text-gray-700 dark:text-yellow-400" /> Credentials</button>}
                            {canEdit && <button onClick={() => handleAddTags(activeAsset!)} className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors duration-200"><Tag className="w-3.5 h-3.5 text-gray-700 dark:text-orange-400" /> Add Tags</button>}
                            {canDelete && <button onClick={() => handleDeleteClick(activeAsset!)} className="w-full text-left px-4 py-2.5 text-[11px] text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 flex items-center gap-3 font-bold"><Trash2 className="w-3.5 h-3.5 text-gray-700 dark:text-red-400" /> Delete Asset</button>}
                        </div>
                    );
                })(),
                document.body
            )}


            {/* Info Popover portal */}
            {infoPopoverAsset && createPortal(
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-3" onClick={() => setInfoPopoverAsset(null)}>
                    <div
                        data-info-popover
                        className="w-full max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[10000] overflow-hidden p-5 text-gray-900 dark:text-white animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4 border-b border-gray-100 dark:border-gray-800 pb-3">
                            <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-2">
                                    <Info className="w-3.5 h-3.5 text-gray-700 dark:text-orange-500" />
                                    <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">Asset Details</h3>
                                </div>
                                <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-1">{infoPopoverAsset.name}</h2>
                            </div>
                            <button onClick={() => setInfoPopoverAsset(null)} className="text-gray-400 hover:text-gray-700 dark:text-gray-200 dark:hover:text-white transition-colors bg-gray-100 dark:bg-gray-800/50 p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer">
                                <X className="w-4 h-4 text-gray-700 dark:text-gray-200" />
                            </button>
                        </div>

                        <div className="space-y-4 custom-scroll max-h-[500px] overflow-y-auto pr-1">
                            {/* Dates Group */}
                            <div>
                                <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5 font-mono">
                                    <Calendar className="w-3.5 h-3.5 text-gray-700 dark:text-orange-500/70" /> Important Dates
                                </h4>
                                <div className="grid grid-cols-2 gap-3 bg-gray-50 dark:bg-gray-800/20 rounded-xl p-3 border border-gray-200 dark:border-gray-700/30">
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[9px] text-gray-500 uppercase font-black tracking-tighter">Purchase</p>
                                        <p className="text-[11px] text-gray-800 dark:text-gray-200 font-bold">{infoPopoverAsset.purchase_date || '--'}</p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[9px] text-gray-500 uppercase font-black tracking-tighter">Installation</p>
                                        <p className="text-[11px] text-gray-800 dark:text-gray-200 font-bold">{infoPopoverAsset.installation_date || '--'}</p>
                                    </div>
                                    <div className="flex flex-col gap-1 border-t border-gray-200 dark:border-gray-800/50 pt-2">
                                        <p className="text-[9px] text-red-500/80 uppercase font-black tracking-tighter">Warranty Exp.</p>
                                        <p className="text-[11px] text-orange-600 dark:text-orange-400 font-black">{infoPopoverAsset.warranty_expiration || '--'}</p>
                                    </div>
                                    <div className="flex flex-col gap-1 border-t border-gray-200 dark:border-gray-800/50 pt-2">
                                        <p className="text-[9px] text-red-500/80 uppercase font-black tracking-tighter">AMC Exp.</p>
                                        <p className="text-[11px] text-orange-600 dark:text-orange-400 font-black">{infoPopoverAsset.amc_expiration || '--'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Warranty & AMC Details */}
                            <div className="space-y-4">
                                <div>
                                    <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 font-mono">
                                        <ShieldIcon className="w-3.5 h-3.5 text-gray-700 dark:text-blue-400/70" /> Warranty Details
                                    </h4>
                                    <div className="bg-blue-50 dark:bg-blue-500/5 rounded-xl p-3 border border-blue-200 dark:border-blue-500/10">
                                        <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-relaxed italic font-medium">
                                            {infoPopoverAsset.warranty_details || 'No specific details documented.'}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 font-mono">
                                        <Briefcase className="w-3.5 h-3.5 text-gray-700 dark:text-amber-500/70" /> AMC Coverage
                                    </h4>
                                    <div className="bg-amber-50 dark:bg-amber-500/5 rounded-xl p-3 border border-amber-200 dark:border-amber-500/10 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[8px] bg-amber-500/20 text-amber-700 dark:text-amber-500 px-1 rounded font-bold uppercase">Provider</span>
                                            <p className="text-[11px] text-amber-900 dark:text-amber-200 font-black">
                                                {infoPopoverAsset.amc_provider || 'Not Listed'}
                                            </p>
                                        </div>
                                        <p className="text-[11px] text-gray-700 dark:text-gray-400 leading-relaxed bg-white/60 dark:bg-black/20 p-2 rounded-lg border border-amber-200/60 dark:border-gray-800/50">
                                            {infoPopoverAsset.amc_details || 'No maintenance contract details found.'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Lifecycle Group */}
                            <div className="pt-1">
                                <h4 className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5 font-mono">
                                    <Box className="w-3.5 h-3.5 text-gray-700 dark:text-red-400/70" /> Lifecycle Status
                                </h4>
                                <div className="grid grid-cols-2 gap-3 bg-red-50 dark:bg-red-500/5 rounded-xl p-3 border border-red-200 dark:border-red-500/10">
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[9px] text-red-500/80 uppercase font-black tracking-tighter">End of Life</p>
                                        <p className="text-[11px] text-gray-800 dark:text-gray-200 font-bold">{infoPopoverAsset.end_of_life || '--'}</p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[9px] text-red-500/80 uppercase font-black tracking-tighter">End of Support</p>
                                        <p className="text-[11px] text-gray-800 dark:text-gray-200 font-bold">{infoPopoverAsset.end_of_support || '--'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}


            {/* Last Frame Modal */}
            {lastFrameModalOpen && createPortal(
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setLastFrameModalOpen(false)}>
                    <div
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/50 rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 bg-teal-500/20 rounded-lg">
                                    <Eye className="w-4 h-4 text-gray-700 dark:text-teal-400" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Last Frame</h3>
                                    <p className="text-[10px] text-gray-500 font-medium">{lastFrameAssetName}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setLastFrameModalOpen(false)}
                                className="text-gray-400 hover:text-gray-700 dark:text-white dark:hover:text-white transition-colors bg-gray-100 dark:bg-gray-800/50 p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-600"
                            >
                                <X className="w-4 h-4 text-gray-700 dark:text-white" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5">
                            {lastFrameLoading ? (
                                <div className="flex flex-col items-center justify-center py-16 gap-3">
                                    <Loader2 className="w-8 h-8 text-gray-700 dark:text-teal-400 animate-spin" />
                                    <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest">Fetching camera frame...</p>
                                </div>
                            ) : lastFrameImage ? (
                                <div className="space-y-3">
                                    <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700/50 bg-black">
                                        <img
                                            src={`data:image/jpeg;base64,${lastFrameImage}`}
                                            alt="Last Camera Frame"
                                            className="w-full h-auto object-contain max-h-[80vh]"
                                        />
                                    </div>
                                    {lastFrameCapturedAt && (
                                        <div className="flex items-center gap-2 text-[10px] text-gray-700 dark:text-white">
                                            <Calendar className="w-3 h-3 text-gray-700 dark:text-white" />
                                            <span className="font-medium">Captured at: <span className="text-gray-900 dark:text-gray-300 font-bold">{new Date(`${lastFrameCapturedAt}z`).toLocaleString()}</span></span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 gap-3">
                                    <Cctv className="w-10 h-10 text-gray-700 dark:text-gray-700" />
                                    <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest">No frame available</p>
                                    <p className="text-[10px] text-gray-400 dark:text-gray-600">This camera has not captured any frames yet.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <AddAssetModal open={assetModalOpen} mode={assetModalMode} asset={selectedAsset} onClose={() => setAssetModalOpen(false)} onSuccess={handleRefresh} />
            <AddCredentialModal open={credentialModalOpen} assetId={credentialAssetId} clientId={credentialClientId} onClose={() => setCredentialModalOpen(false)} isCamera={isCamera} initialAgentId={initialAgentId} />
            <AddTagModal open={tagModalOpen} assetId={tagAssetId} assetName={tagAssetName} onClose={() => setTagModalOpen(false)} onSuccess={handleRefresh} />
            <ScanModal open={scanModalOpen} asset={selectedScanAsset} onClose={() => { setScanModalOpen(false); setSelectedScanAsset(null); }} />
            <DeleteConfirmationModal open={deleteModalOpen} isDeleting={isDeleting} assetName={assetToDelete?.name} onClose={() => { setDeleteModalOpen(false); setAssetToDelete(null); }} onConfirm={confirmDelete} />
            <UploadModal open={importModal} onClose={() => setImportModal(false)} onSuccess={handleRefresh} />
            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="assets"
                clientId={level?.id}
                asset_type={typeFilter === "other"
                    ? "unknown"
                    : typeFilter}
            />
        </div>
    );
};


export default AssetsTable;
